import { useApi, ApiError, useT, useLocale, timeAgo } from "@iga/ui";
import type { Tasks, Campaign, Risk } from "./types";

export const TASKS_KEY = ["me-tasks"];

/** Task counters shown on Home, in the Tasks inbox and on the shell badge; refreshed after every mutation. */
export function useTasks() {
  return useApi<Tasks>(TASKS_KEY, "/api/portal/me/tasks", undefined, { staleTime: 30_000, refetchInterval: 60_000 });
}

/** Active review campaigns where the user is a reviewer (needs a linked identity). */
export function useCampaigns(enabled: boolean) {
  return useApi<{ items: Campaign[] }>(["certifications"], "/api/portal/certifications", undefined, { enabled, staleTime: 30_000 });
}

export function errMsg(e: unknown, fallback = "Something went wrong"): string {
  if (e instanceof ApiError) return e.message || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}

/** All query keys touched by a request/approval/certification mutation – invalidated together so counts and lists stay in sync. */
export const REQUEST_KEYS: unknown[][] = [TASKS_KEY, ["my-requests"], ["request"], ["approvals"], ["approvals-history"], ["catalog"], ["me-access"], ["team"], ["team-access"], ["sd-identity"]];
export const CERT_KEYS: unknown[][] = [TASKS_KEY, ["certifications"], ["cert-items"]];

/** Relative time in both directions ("in 3 days", "2 hours ago") in the active locale. */
/** Intl tag for the active locale: British English gives "Fri 10 Oct" and 24-hour times (§7). */
export function useIntlLocale(): string {
  const locale = useLocale();
  return locale === "en" ? "en-GB" : locale === "da" ? "da-DK" : locale;
}

export function useRelTime() {
  const locale = useIntlLocale();
  return (v?: string | null): string => timeAgo(v, locale);
}

/** Portal date formats (§7): "Fri 10 Oct", "Fri 10 Oct 09:14", in the active locale. */
export function useFmt() {
  const locale = useIntlLocale();
  const day = (v?: string | null) => (v ? new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" }).format(new Date(v)) : "–");
  const dayTime = (v?: string | null) => (v ? new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(v)) : "–");
  const time = (v?: string | null) => (v ? new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(v)) : "–");
  const num = (n?: number | null) => new Intl.NumberFormat(locale).format(n ?? 0);
  return { day, dayTime, time, num };
}

export function daysUntil(v?: string | null): number | null {
  if (!v) return null;
  return Math.ceil((new Date(v).getTime() - Date.now()) / 86_400_000);
}

export const firstName = (name?: string | null) => (name ?? "").trim().split(/\s+/)[0] ?? "";

/** Risk words as a 1..4 rank, for "risk ≤ medium" guards. */
export const riskRank: Record<Risk, number> = { low: 1, medium: 2, high: 3, critical: 4 };

/** Translated risk sentence for the portal ("High risk"). */
export function useRiskWord() {
  const t = useT();
  return (risk?: Risk | null) => t(`risk.${riskRank[risk ?? "low"]}`);
}
